import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_URL as API } from '../lib/api';
import {
  filterConfirmedTournamentRegistrations,
  filterJoinedTournamentRegistrations,
} from '../lib/tournamentRegistrations';
import useTournamentRealtime from './useTournamentRealtime';
import useRealtimeEvents from './useRealtimeEvents';

const WITHDRAW_NOTIF_EVENTS = new Set(['notification_created']);

/**
 * Load the signed-in member's tournament registrations and keep them live.
 * Public tournament_updated frames omit PII — we refetch my-registrations.
 */
export default function useMyTournamentRegistrations() {
  const { token, user } = useAuth();
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const refreshTimerRef = useRef(0);

  const refresh = useCallback(async ({ silent } = {}) => {
    if (!token) {
      setRegistrations([]);
      return [];
    }
    if (!silent) setLoading(true);
    try {
      const res = await fetch(`${API}/tournaments/my-registrations`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (!res.ok) {
        setRegistrations([]);
        return [];
      }
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setRegistrations(list);
      return list;
    } catch {
      setRegistrations([]);
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token]);

  const scheduleRefresh = useCallback(() => {
    window.clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = window.setTimeout(() => {
      refresh({ silent: true });
    }, 400);
  }, [refresh]);

  useEffect(() => {
    refresh();
    return () => window.clearTimeout(refreshTimerRef.current);
  }, [refresh]);

  useTournamentRealtime(
    useCallback(() => {
      if (!token) return;
      scheduleRefresh();
    }, [token, scheduleRefresh]),
    { enabled: Boolean(token) },
  );

  useRealtimeEvents(
    WITHDRAW_NOTIF_EVENTS,
    useCallback((msg) => {
      if (!user?.user_id || msg.user_id !== user.user_id) return;
      const type = String(msg.notification?.type || '').toLowerCase();
      if (type !== 'tournament_withdrawal' && type !== 'tournament_partner_change') return;
      scheduleRefresh();
    }, [user?.user_id, scheduleRefresh]),
    { enabled: Boolean(token && user?.user_id) },
  );

  const confirmedRegistrations = useMemo(
    () => filterConfirmedTournamentRegistrations(registrations),
    [registrations],
  );

  const joinedRegistrations = useMemo(
    () => filterJoinedTournamentRegistrations(registrations),
    [registrations],
  );

  return {
    registrations,
    confirmedRegistrations,
    joinedRegistrations,
    confirmedCount: confirmedRegistrations.length,
    loading,
    refresh,
  };
}
